import React, { useCallback, useEffect, useRef, useState } from "react";
import { FileIndex, UserSession } from "../types";
import { Row, Form } from "react-bootstrap";
import MediaFilter from "./mediaFilter";
import UploadButton from "./uploadButton";
import ListGroup from "react-bootstrap/ListGroup";
import { Stack, Button } from "react-bootstrap";

const formatDuration = (seconds: number): string => {
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  } else {
    return `${m}:${String(s).padStart(2, "0")}`;
  }
};

const FileTree: React.FC<{
  fileList: FileIndex[];
  refreshFileList: () => Promise<void>;
  selectedItem: FileIndex | undefined;
  setSelectedItem: (item: FileIndex | undefined) => void;
  videoFinished: boolean;
  userSession: UserSession;
  updateUserSession: (userSession: UserSession) => void;
}> = ({
  fileList,
  refreshFileList,
  selectedItem,
  setSelectedItem,
  videoFinished,
  userSession,
  updateUserSession,
}) => {
  const [nameFilter, setNameFilter] = useState<string>(userSession.searchText);
  const [displayedItems, setDisplayedItems] = useState<FileIndex[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [treePath, setTreePath] = useState<FileIndex[]>([]);

  // Scroll the item after the selected one into view so the selected item
  // is visible with one item of context below it.
  const scrollAnchorRef = useCallback(
    (node: HTMLElement | null) => {
      if (node) {
        node.scrollIntoView({ block: "nearest" });
      }
    },
    [selectedItem],
  );

  useEffect(() => {
    if (fileList.length > 0) {
      var filteredFiles = fileList;
      const selectionPath = [];
      for (let selected of userSession.selectedFileNames) {
        for (let item of filteredFiles) {
          if (item.fileName === selected) {
            if (item.fileType === "folder") {
              selectionPath.push(item);
              filteredFiles = item.children;
            } else if (selectedItem === undefined) {
              setSelectedItem(item);
            }
          }
        }
      }
      setTreePath(selectionPath);

      const itemsAtCurrentLevel = filteredFiles;
      setTotalItems(itemsAtCurrentLevel.length);

      if (userSession.typeFilter !== "all") {
        filteredFiles = filteredFiles.filter(
          (x) => x.fileType === userSession.typeFilter,
        );
      }
      if (userSession.searchText !== "") {
        filteredFiles = filteredFiles.filter(
          (x) =>
            x.displayName.toUpperCase().indexOf(nameFilter.toUpperCase()) > -1,
        );
      }
      if (treePath.length === 0) {
        const sortedFiles = filteredFiles.sort((a, b) =>
          a.displayName.toLowerCase() < b.displayName.toLowerCase() ? -1 : 1,
        );
        setDisplayedItems(sortedFiles);
      } else {
        setDisplayedItems(filteredFiles);
      }
      // if (selectedItem === undefined && userSession.selectedFileIndex >= 0) {
      //   selectItem(
      //     userSession.selectedFileIndex,
      //     sortedFiles[userSession.selectedFileIndex]
      //   );
      // }
    }
  }, [fileList, nameFilter, userSession, setSelectedItem, treePath.length]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      updateUserSession({ ...userSession, searchText: nameFilter });
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [nameFilter]);

  const randomIndex = () => {
    return Math.floor(Math.random() * (displayedItems.length - 0 + 1));
  };

  useEffect(() => {
    if (videoFinished && selectedItem !== undefined) {
      if (userSession.autoplay && userSession.shuffle) {
        var idx = randomIndex();
        while (userSession.playedIndices.includes(idx)) {
          idx = randomIndex();
        }
        const newItem = displayedItems[idx];
        updateUserSession({
          ...userSession,
          playedSeconds: 0,
          playedIndices: [...userSession.playedIndices, idx],
          selectedFileNames: [
            ...treePath.map((i) => i.fileName),
            newItem.fileName,
          ],
        });
      } else if (userSession.autoplay) {
        const currIdx = displayedItems.indexOf(selectedItem);
        selectItem(displayedItems[currIdx + 1]);
      }
    }
  }, [videoFinished]);

  const selectItem = (item: FileIndex | undefined) => {
    // If user clicks "Library" link
    if (item === undefined) {
      setTreePath([]);
      updateUserSession({
        ...userSession,
        playedIndices: [],
        selectedFileNames: [],
      });
      return;
    }
    if (item.fileType === "folder") {
      setTreePath([...treePath, item]);
      setSelectedItem(undefined);
      setDisplayedItems(item.children);
      setTotalItems(item.children.length);
    } else {
      setSelectedItem(item);
    }
    updateUserSession({
      ...userSession,
      playedSeconds: 0,
      playedIndices: [],
      selectedFileNames: [...treePath.map((i) => i.fileName), item.fileName],
    });
  };

  return (
    <div className="h-100 w-100 d-inline-flex flex-column">
      <Row>
        <Stack direction="horizontal" gap={1} className="py-1">
          <Form.Control
            placeholder="Search"
            className="mx-1"
            value={nameFilter}
            onChange={(e) => {
              setNameFilter(e.target.value);
            }}
          />
          <small className="text-muted text-nowrap">
            ({displayedItems.length}/{totalItems})
          </small>
          <MediaFilter
            userSession={userSession}
            updateUserSession={updateUserSession}
          />
          <UploadButton
            refreshFileList={refreshFileList}
            treePath={treePath}
            canDeleteFolder={fileList.length === 0}
          />
        </Stack>
      </Row>
      <Stack direction="horizontal">
        <Button variant="link" onClick={() => selectItem(undefined)}>
          Library
        </Button>
        {treePath.map((item) => (
          <div key={item.fileName}>
            -&gt;
            <Button variant="link">{item.displayName}</Button>
          </div>
        ))}
      </Stack>
      <hr className="m-1" />
      <div className="flex-fill overflow-hidden">
        {displayedItems.length === 0 ? (
          <div>No files matching filters</div>
        ) : (
          <ListGroup
            variant="flush"
            className="h-100 overflow-auto text-wrap"
            style={{ overflowWrap: "break-word" }}
          >
            {(() => {
              const selectedIdx = displayedItems.findIndex(
                (i) => i.fileName === selectedItem?.fileName,
              );
              const anchorIdx =
                selectedIdx >= 0
                  ? selectedIdx + 1 < displayedItems.length
                    ? selectedIdx + 1
                    : selectedIdx
                  : -1;

              return displayedItems.map((item, idx) => (
                <ListGroup.Item
                  key={item.fileName}
                  ref={idx === anchorIdx ? scrollAnchorRef : null}
                  active={item.fileName === selectedItem?.fileName}
                  onClick={() => selectItem(item)}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <span>{item.displayName}</span>
                    {item.durationSeconds != null && (
                      <small
                        className="text-nowrap ms-2"
                        style={{ opacity: 0.6, fontSize: "0.75em" }}
                      >
                        {formatDuration(item.durationSeconds)}
                      </small>
                    )}
                  </div>
                </ListGroup.Item>
              ));
            })()}
          </ListGroup>
        )}
      </div>
    </div>
  );
};
export default FileTree;
